import type {
  ApprovalMutationResponse,
  ApprovalRequest,
  AgentPlanResponse,
  AgentRunResponse,
  AuthSession,
  BetaOverview,
  DashboardResponse,
  DeploymentCreateInput,
  DeploymentItem,
  DeploymentMutationResponse,
  DeploymentUpdateInput,
  DraftQodeChanges,
  HealthCheckItem,
  HealthCheckMutationResponse,
  FallbackValidationResponse,
  IncidentDetailResponse,
  IncidentDiagnosisResponse,
  IncidentItem,
  IncidentMutationResponse,
  IncidentRemediationResponse,
  MemberMutationResponse,
  MembersResponse,
  NotificationsResponse,
  NotificationItem,
  OperationsSummary,
  PolicyResponse,
  PublishResponse,
  QodeDetailResponse,
  QodeMutationResponse,
  QodeVersionResponse,
  RegistrationResponse,
  RollbackResponse,
  SupportDiagnostics,
  WorkspaceMember,
  WorkspaceMembership,
  WorkspaceRole,
} from "./types";

type ProblemDetails = {
  detail?: string;
  title?: string;
};

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let problem: ProblemDetails | null = null;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // Keep the status-based fallback when a proxy returns a non-JSON error.
    }
    if (response.status === 401 && path !== "/api/auth/login") {
      window.dispatchEvent(new Event("qode:unauthorized"));
    }
    throw new ApiError(
      problem?.detail ?? problem?.title ?? `请求失败（${response.status}）`,
      response.status,
    );
  }

  return (await response.json()) as T;
}

export function getCurrentSession(): Promise<AuthSession> {
  return apiRequest<AuthSession>("/api/auth/me");
}

export function login(
  username: string,
  password: string,
): Promise<AuthSession> {
  return apiRequest<AuthSession>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export function register(input: {
  workspaceName: string;
  displayName: string;
  username: string;
  email: string;
  password: string;
  acceptTerms: true;
  termsVersion: "2026-07-24";
  privacyVersion: "2026-07-24";
  website: "";
}): Promise<RegistrationResponse> {
  return apiRequest<RegistrationResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function verifyEmail(
  token: string,
): Promise<{ status: "email_verified"; requestId: string }> {
  return apiRequest("/api/auth/email/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function resendVerification(
  email: string,
): Promise<{ status: "verification_requested"; requestId: string }> {
  return apiRequest("/api/auth/email/resend", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function requestPasswordReset(
  email: string,
): Promise<{ status: "password_reset_requested"; requestId: string }> {
  return apiRequest("/api/auth/password/reset/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<{ status: "password_reset"; requestId: string }> {
  return apiRequest("/api/auth/password/reset/confirm", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
}

export function logout(): Promise<{ status: string }> {
  return apiRequest<{ status: string }>("/api/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ status: string }> {
  return apiRequest<{ status: string }>("/api/auth/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function getWorkspaces(): Promise<{
  workspaces: WorkspaceMembership[];
  requestId: string;
}> {
  return apiRequest("/api/workspaces");
}

export function switchWorkspace(workspaceId: string): Promise<AuthSession> {
  return apiRequest<AuthSession>("/api/auth/workspace", {
    method: "POST",
    body: JSON.stringify({ workspaceId }),
  });
}

export function getMembers(): Promise<MembersResponse> {
  return apiRequest<MembersResponse>("/api/members");
}

export function createMember(input: {
  username: string;
  displayName: string;
  role: WorkspaceRole;
}): Promise<MemberMutationResponse> {
  return apiRequest<MemberMutationResponse>("/api/members", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateMember(
  userId: string,
  changes: Partial<Pick<WorkspaceMember, "role" | "status">>,
): Promise<MemberMutationResponse> {
  return apiRequest<MemberMutationResponse>(`/api/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
}

export function getApprovals(): Promise<{
  approvals: ApprovalRequest[];
  requestId: string;
}> {
  return apiRequest("/api/approvals");
}

export function getDeployments(): Promise<{
  deployments: DeploymentItem[];
  requestId: string;
}> {
  return apiRequest("/api/deployments");
}

export function createDeployment(
  input: DeploymentCreateInput,
): Promise<DeploymentMutationResponse> {
  return apiRequest<DeploymentMutationResponse>("/api/deployments", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDeployment(
  deploymentId: string,
  input: DeploymentUpdateInput,
): Promise<DeploymentMutationResponse> {
  return apiRequest<DeploymentMutationResponse>(
    `/api/deployments/${deploymentId}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function getPolicy(): Promise<PolicyResponse> {
  return apiRequest<PolicyResponse>("/api/policy");
}

export function getIncidents(): Promise<{
  incidents: IncidentItem[];
  requestId: string;
}> {
  return apiRequest("/api/incidents");
}

export function getIncidentDetail(
  incidentId: string,
): Promise<IncidentDetailResponse> {
  return apiRequest<IncidentDetailResponse>(`/api/incidents/${incidentId}`);
}

export function diagnoseIncident(
  incidentId: string,
): Promise<IncidentDiagnosisResponse> {
  return apiRequest<IncidentDiagnosisResponse>(
    `/api/incidents/${incidentId}/diagnose`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function validateIncidentFallback(
  incidentId: string,
  destination: string,
): Promise<FallbackValidationResponse> {
  return apiRequest<FallbackValidationResponse>(
    `/api/incidents/${incidentId}/fallback/validate`,
    {
      method: "POST",
      body: JSON.stringify({ destination }),
    },
  );
}

export function getOperationsSummary(): Promise<
  OperationsSummary & { requestId: string }
> {
  return apiRequest("/api/operations/summary");
}

export function getNotifications(): Promise<NotificationsResponse> {
  return apiRequest<NotificationsResponse>("/api/notifications");
}

export function getBetaOverview(): Promise<BetaOverview> {
  return apiRequest<BetaOverview>("/api/beta");
}

export function createCustomDomain(input: {
  qodeId: string;
  hostname: string;
}): Promise<BetaOverview> {
  return apiRequest<BetaOverview>("/api/custom-domains", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function upsertHostedPage(input: {
  qodeId: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  status: "draft" | "published";
}): Promise<BetaOverview> {
  return apiRequest<BetaOverview>("/api/hosted-pages", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function importQodes(
  items: Array<{
    name: string;
    purpose: string;
    destination: string;
    slug?: string;
  }>,
): Promise<{ created: Array<{ id: string; slug: string; name: string }>; count: number }> {
  return apiRequest("/api/import", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export function getSupportDiagnostics(): Promise<SupportDiagnostics> {
  return apiRequest<SupportDiagnostics>("/api/support/diagnostics");
}

export function markNotificationRead(
  notificationId: string,
): Promise<{ notification: NotificationItem; requestId: string }> {
  return apiRequest(`/api/notifications/${notificationId}/read`, {
    method: "PATCH",
    body: JSON.stringify({}),
  });
}

export function getHealthChecks(): Promise<{
  healthChecks: HealthCheckItem[];
  requestId: string;
}> {
  return apiRequest("/api/health-checks");
}

export function runQodeHealthCheck(
  qodeId: string,
): Promise<HealthCheckMutationResponse> {
  return apiRequest<HealthCheckMutationResponse>(
    `/api/qodes/${qodeId}/check`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function acknowledgeIncident(
  incidentId: string,
): Promise<IncidentMutationResponse> {
  return apiRequest<IncidentMutationResponse>(
    `/api/incidents/${incidentId}/acknowledge`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function requestIncidentRemediation(
  incidentId: string,
  destination: string,
): Promise<IncidentRemediationResponse> {
  return apiRequest<IncidentRemediationResponse>(
    `/api/incidents/${incidentId}/remediation`,
    {
      method: "POST",
      body: JSON.stringify({ destination }),
    },
  );
}

export function updatePolicy(input: {
  requireIndependentApproval: boolean;
  highImpactThreshold: number;
}): Promise<PolicyResponse> {
  return apiRequest<PolicyResponse>("/api/policy", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function requestQodeApproval(
  qodeId: string,
  input: {
    actionType: "publish" | "rollback";
    destination?: string;
    releaseId?: string;
    reason?: string;
  },
): Promise<ApprovalMutationResponse> {
  return apiRequest<ApprovalMutationResponse>(
    `/api/qodes/${qodeId}/approvals`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function approveRequest(
  approvalId: string,
): Promise<ApprovalMutationResponse> {
  return apiRequest<ApprovalMutationResponse>(
    `/api/approvals/${approvalId}/approve`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function rejectRequest(
  approvalId: string,
  reason: string,
): Promise<ApprovalMutationResponse> {
  return apiRequest<ApprovalMutationResponse>(
    `/api/approvals/${approvalId}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export function getDashboard(): Promise<DashboardResponse> {
  return apiRequest<DashboardResponse>("/api/qodes");
}

export function createAgentPlan(goal: string): Promise<AgentPlanResponse> {
  return apiRequest<AgentPlanResponse>("/api/agent/plan", {
    method: "POST",
    body: JSON.stringify({ goal }),
  });
}

export function createAgentRun(goal: string): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>("/api/agent/runs", {
    method: "POST",
    body: JSON.stringify({ goal }),
  });
}

export function getAgentRun(runId: string): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>(`/api/agent/runs/${runId}`);
}

export function executeAgentRun(
  runId: string,
  reason: string,
  expectedQodeUpdatedAt?: string,
): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>(`/api/agent/runs/${runId}/execute`, {
    method: "POST",
    body: JSON.stringify({ reason, expectedQodeUpdatedAt }),
  });
}

export function resumeAgentRun(
  runId: string,
  additionalContext: string,
): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>(`/api/agent/runs/${runId}/resume`, {
    method: "POST",
    body: JSON.stringify({ additionalContext }),
  });
}

export function retryAgentRun(runId: string): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>(`/api/agent/runs/${runId}/retry`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function cancelAgentRun(
  runId: string,
  reason: string,
): Promise<AgentRunResponse> {
  return apiRequest<AgentRunResponse>(`/api/agent/runs/${runId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function subscribeAgentRunEvents(
  runId: string,
  handlers: {
    onRun: (run: AgentRunResponse["run"]) => void;
    onError?: () => void;
  },
): () => void {
  const source = new EventSource(`/api/agent/runs/${runId}/events`);
  source.addEventListener("run", (event) => {
    handlers.onRun(JSON.parse((event as MessageEvent<string>).data));
  });
  source.addEventListener("done", () => source.close());
  source.onerror = () => {
    source.close();
    handlers.onError?.();
  };
  return () => source.close();
}

export function getQodeDetail(qodeId: string): Promise<QodeDetailResponse> {
  return apiRequest<QodeDetailResponse>(`/api/qodes/${qodeId}`);
}

export function createQodeVersion(
  qodeId: string,
  destination: string,
): Promise<QodeVersionResponse> {
  return apiRequest<QodeVersionResponse>(`/api/qodes/${qodeId}/versions`, {
    method: "POST",
    body: JSON.stringify({ destination }),
  });
}

export function validateQodeVersion(
  versionId: string,
): Promise<QodeVersionResponse> {
  return apiRequest<QodeVersionResponse>(
    `/api/qode-versions/${versionId}/validate`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function markQodeVersionReady(
  versionId: string,
): Promise<QodeVersionResponse> {
  return apiRequest<QodeVersionResponse>(
    `/api/qode-versions/${versionId}/ready`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function publishQodeVersion(
  versionId: string,
  reason?: string,
): Promise<PublishResponse> {
  return apiRequest<PublishResponse>(
    `/api/qode-versions/${versionId}/publish`,
    {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
}

export function updateDraftQode(
  qodeId: string,
  changes: DraftQodeChanges,
): Promise<QodeMutationResponse> {
  return apiRequest<QodeMutationResponse>(`/api/qodes/${qodeId}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
}

export function publishQode(
  qodeId: string,
  destination?: string,
  reason?: string,
): Promise<PublishResponse> {
  return apiRequest<PublishResponse>(`/api/qodes/${qodeId}/publish`, {
    method: "POST",
    body: JSON.stringify({
      ...(destination ? { destination } : {}),
      ...(reason ? { reason } : {}),
    }),
  });
}

export function pauseQode(qodeId: string): Promise<QodeMutationResponse> {
  return apiRequest<QodeMutationResponse>(`/api/qodes/${qodeId}/pause`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function resumeQode(qodeId: string): Promise<QodeMutationResponse> {
  return apiRequest<QodeMutationResponse>(`/api/qodes/${qodeId}/resume`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function rollbackQode(
  qodeId: string,
  releaseId: string,
  reason?: string,
): Promise<RollbackResponse> {
  return apiRequest<RollbackResponse>(`/api/qodes/${qodeId}/rollback`, {
    method: "POST",
    body: JSON.stringify({ releaseId, ...(reason ? { reason } : {}) }),
  });
}
